/**
 * VistoeraTASK-00685 — Image Assets viewer folder-scoped prev/next helpers.
 *
 * Navigation stays inside the same list query the user had when opening the
 * viewer (folder / view / sort / filters). Cross-page browsing reuses
 * `listImageLibrary` offset pagination — never a second query system.
 */

import type { ImageLibraryView, ImageOrigin, LibraryAssetItem } from '../../api/client'

export type ImageAssetViewerNavQuery = {
  q: string
  origin: 'all' | ImageOrigin
  sort: 'recent' | 'oldest' | 'name'
  pageSize: number
  /** When false, team-only params are omitted (personal Workspace). */
  teamEnabled: boolean
  view: ImageLibraryView
  creatorFilter: string
  collectionId: string | null
}

export type ImageAssetViewerNavSession = {
  query: ImageAssetViewerNavQuery
  total: number
  absoluteIndex: number
}

export function pickerItemKey(item: Pick<LibraryAssetItem, 'id' | 'picker_item_id'>): string {
  if (item.picker_item_id) return item.picker_item_id
  if (item.id.startsWith('sa:') || item.id.startsWith('lib:')) return item.id
  return `lib:${item.id}`
}

export function findItemIndexInPage(
  items: readonly LibraryAssetItem[],
  item: Pick<LibraryAssetItem, 'id' | 'picker_item_id'>,
): number {
  const key = pickerItemKey(item)
  return items.findIndex((i) => i.id === item.id || pickerItemKey(i) === key)
}

export function absoluteIndexFromPage(
  page: number,
  pageSize: number,
  indexInPage: number,
): number {
  if (page < 1 || pageSize < 1 || indexInPage < 0) return -1
  return (page - 1) * pageSize + indexInPage
}

export function pageForAbsoluteIndex(absoluteIndex: number, pageSize: number): number {
  if (absoluteIndex < 0 || pageSize < 1) return 1
  return Math.floor(absoluteIndex / pageSize) + 1
}

export function indexInPageForAbsolute(absoluteIndex: number, pageSize: number): number {
  if (absoluteIndex < 0 || pageSize < 1) return -1
  return absoluteIndex % pageSize
}

export function canGoPrev(absoluteIndex: number): boolean {
  return absoluteIndex > 0
}

/** No wrap-around: last item has no next. */
export function canGoNext(absoluteIndex: number, total: number): boolean {
  return total > 0 && absoluteIndex >= 0 && absoluteIndex < total - 1
}

export function buildLibraryListParams(
  query: ImageAssetViewerNavQuery,
  page: number,
): {
  q?: string
  origin?: ImageOrigin
  page: number
  page_size: number
  sort: 'recent' | 'oldest' | 'name'
  view?: ImageLibraryView
  creator_id?: string
  collection_id?: string
} {
  const params: {
    q?: string
    origin?: ImageOrigin
    page: number
    page_size: number
    sort: 'recent' | 'oldest' | 'name'
    view?: ImageLibraryView
    creator_id?: string
    collection_id?: string
  } = {
    page,
    page_size: query.pageSize,
    sort: query.sort,
  }
  const trimmed = query.q.trim()
  if (trimmed) params.q = trimmed
  if (query.origin !== 'all') params.origin = query.origin
  if (query.teamEnabled) {
    params.view = query.view
    if (query.creatorFilter !== 'all') params.creator_id = query.creatorFilter
    if (query.collectionId) params.collection_id = query.collectionId
  }
  return params
}

export function snapshotNavQuery(input: {
  q: string
  origin: 'all' | ImageOrigin
  sort: 'recent' | 'oldest' | 'name'
  pageSize: number
  teamEnabled: boolean
  view: ImageLibraryView
  creatorFilter: string
  collectionId: string | null
}): ImageAssetViewerNavQuery {
  return {
    q: input.q,
    origin: input.origin,
    sort: input.sort,
    pageSize: input.pageSize,
    teamEnabled: input.teamEnabled,
    view: input.view,
    creatorFilter: input.creatorFilter,
    collectionId: input.collectionId,
  }
}

/** Build a nav session from the current list page, or null when the item is not in it. */
export function createNavSessionFromPage(args: {
  items: readonly LibraryAssetItem[]
  item: LibraryAssetItem
  page: number
  pageSize: number
  total: number
  query: ImageAssetViewerNavQuery
}): ImageAssetViewerNavSession | null {
  const indexInPage = findItemIndexInPage(args.items, args.item)
  if (indexInPage < 0) return null
  const absoluteIndex = absoluteIndexFromPage(args.page, args.pageSize, indexInPage)
  if (absoluteIndex < 0) return null
  return {
    query: args.query,
    total: Math.max(0, args.total),
    absoluteIndex,
  }
}
